import { useState } from "react";
import { filterGroups } from "@/mocks/home";

interface FilterBarProps {
  onNotify: (message: string) => void;
}

export default function FilterBar({ onNotify }: FilterBarProps) {
  const [selected, setSelected] = useState<Record<string, string[]>>({});

  const toggle = (groupId: string, option: string) => {
    setSelected((prev) => {
      const current = prev[groupId] ?? [];
      const next = current.includes(option)
        ? current.filter((o) => o !== option)
        : [...current, option];
      return { ...prev, [groupId]: next };
    });
  };

  const total = Object.values(selected).reduce((sum, arr) => sum + arr.length, 0);

  const reset = () => {
    setSelected({});
    onNotify("已清空筛选条件");
  };

  const apply = () => {
    if (total === 0) {
      onNotify("先勾选几个条件，再帮你缩小范围");
      return;
    }
    onNotify(`已按 ${total} 个条件筛选出适合深夜的片单`);
  };

  return (
    <section className="w-full border-b border-background-300/40 bg-background-100/60">
      <div className="mx-auto w-full max-w-[1180px] px-4 py-8 md:px-6">
        <div className="flex flex-col gap-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-foreground-500">
              <i className="ri-equalizer-2-line text-base text-primary-300" />
              <span>快捷找片 · 勾一勾，范围立刻小一圈</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={reset}
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300/60 px-3 py-1.5 text-xs text-foreground-500 transition-colors hover:text-foreground-900"
              >
                清空
              </button>
              <button
                type="button"
                onClick={apply}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-3.5 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-secondary-600"
              >
                <i className="ri-filter-3-line" />
                应用筛选{total > 0 ? ` (${total})` : ""}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {filterGroups.map((group) => (
              <div key={group.id} className="rounded-lg border border-background-300/50 bg-background-50/60 p-4">
                <p className="mb-3 text-xs tracking-wide text-foreground-500">{group.title}</p>
                <div className="flex flex-wrap gap-2">
                  {group.options.map((option) => {
                    const active = (selected[group.id] ?? []).includes(option);
                    return (
                      <button
                        key={option}
                        type="button"
                        onClick={() => toggle(group.id, option)}
                        className={
                          active
                            ? "cursor-pointer whitespace-nowrap rounded-full border border-primary-500/50 bg-primary-500/15 px-3 py-1 text-xs text-primary-200"
                            : "cursor-pointer whitespace-nowrap rounded-full border border-background-300/60 px-3 py-1 text-xs text-foreground-500 transition-colors hover:border-primary-400 hover:text-primary-300"
                        }
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}